import { useCallback, useEffect } from 'react';
import { Navigation, NavigationNode } from '~/lib/data-contract';
import Image from './Image';
import ArrowLeft from '~/icons/chevron-left.svg';
import Close from '~/icons/close.svg';
import styles from './MegaMenu.module.scss';
import { Link } from '~/shared/components';
import { usePathname } from 'next/navigation';
import Chevron from '~/icons/chevron.svg';
import Typography from '~/shared/components/Typography/Typography';

const DATA_THIRD_MENU = 'data-third-menu-open';

export const closeMenu = () => {
    document.documentElement.removeAttribute('data-menu-open');
    document.documentElement.removeAttribute(DATA_THIRD_MENU);
    document
        .querySelector(`.${styles.subNavItem}.${styles.active}`)
        ?.classList.remove(styles.active);
    document
        .querySelector(`.${styles.mainNavItem}.${styles.active}`)
        ?.classList.remove(styles.active);
};

const MegaMenu = ({ navigation }: { navigation?: Navigation }) => {
    const pathname = usePathname();

    const { mainMenu = [], metaMenu } = navigation || {};

    useEffect(() => {
        const mainNavigation = document.getElementById('mainNavigation');
        const mainHeader = document.getElementById('mainHeader');

        if (!mainNavigation || !mainHeader) return;

        const resizeObserver = new ResizeObserver((entries) => {
            const entry = entries[0];
            document.documentElement.style.setProperty(
                '--mainNavigation-height',
                `${entry.contentRect.height}px`,
            );
        });

        const resizeObserverMainHeader = new ResizeObserver((entries) => {
            const entry = entries[0];
            document.documentElement.style.setProperty(
                '--mainHeader-height',
                `${entry.contentRect.height}px`,
            );
        });

        resizeObserver.observe(mainNavigation);
        resizeObserverMainHeader.observe(mainHeader);

        return () => {
            resizeObserver.disconnect();
            resizeObserverMainHeader.disconnect();
        };
    }, []);

    useEffect(() => {
        if (typeof window !== 'undefined') closeMenu();
    }, [pathname]);

    const navHeader = useCallback((data: NavigationNode, onClickBack?: () => void) => {
        return (
            <figure className={styles.navHeader}>
                <button className={styles.backButton} onClick={onClickBack}>
                    <ArrowLeft />
                </button>

                <button className={styles.closeButton} onClick={closeMenu}>
                    <Close />
                </button>

                <Image src={data.image?.src ?? ''} height={321} width={834} sizes="100vw" alt="" />

                <figcaption>
                    {data.link.title}
                    <Link href={data.link.url}>{data.alternateLinkHeader}</Link>
                </figcaption>
            </figure>
        );
    }, []);

    const thirdLevelItems = (data?: NavigationNode) => {
        if (!data?.children?.length) return null;

        return (
            <div className={styles.subSubNav}>
                {navHeader(data, () => {
                    document
                        .querySelector(`.${styles.subNavItem}.${styles.active}`)
                        ?.classList.remove(styles.active);

                    document.documentElement.removeAttribute(DATA_THIRD_MENU);
                })}

                <ul className={styles.subSubNavList}>
                    {data.children.map((item, index) => (
                        <li key={index} className={styles.subSubNavItem}>
                            <Link href={item.link.url} className={styles.subSubNavLink}>
                                {item.link?.text}

                                {!!item.image?.src && (
                                    <Image
                                        className={styles.linkImage}
                                        height={100}
                                        width={100}
                                        src={item.image?.src as string}
                                        alt={item.image?.alt ?? 'Navigation image'}
                                    />
                                )}
                            </Link>
                        </li>
                    ))}
                </ul>
            </div>
        );
    };

    const secondLevelItems = (data?: NavigationNode, dataIndex?: number) => {
        if (!data?.children?.length) return null;

        return (
            <div
                className={styles.subNav}
                tabIndex={-1}
                data-testid={`dropdown-container-${dataIndex}`}
            >
                {navHeader(data, () =>
                    document
                        .querySelector(`.${styles.mainNavItem}.${styles.active}`)
                        ?.classList.remove(styles.active),
                )}

                <ul className={styles.subNavList}>
                    {data.children.map((item, index) => (
                        <li key={index} className={styles.subNavItem}>
                            <div className={styles.subNavImage}>
                                <Image
                                    className={styles.image}
                                    height={500}
                                    width={500}
                                    sizes="25vw"
                                    src={item.image?.src as string}
                                    alt={item.image?.alt ?? 'Navigation image'}
                                />
                            </div>

                            {!!item.children?.length && (
                                <div
                                    className={styles.subNavLinkMenu}
                                    onClick={(e) => {
                                        e.currentTarget.parentElement?.classList.add(styles.active);
                                        document.documentElement.setAttribute(DATA_THIRD_MENU, '');
                                    }}
                                >
                                    {item.link.title}

                                    {!!item.mobileImage?.src && (
                                        <Image
                                            className={styles.linkImage}
                                            height={100}
                                            width={100}
                                            src={item.mobileImage?.src as string}
                                            alt={item.mobileImage?.alt ?? 'Navigation image'}
                                        />
                                    )}
                                </div>
                            )}

                            <Link href={item.link.url} className={styles.subNavLink}>
                                {item.link.title}

                                {!!item.mobileImage?.src && (
                                    <Image
                                        className={styles.linkImage}
                                        height={100}
                                        width={100}
                                        src={item.mobileImage?.src as string}
                                        alt={item.mobileImage?.alt ?? 'Navigation image'}
                                    />
                                )}
                            </Link>

                            {thirdLevelItems(item)}
                        </li>
                    ))}
                </ul>
            </div>
        );
    };

    return (
        <nav className={styles.component} data-testid="main-menu" aria-label="navigation">
            <ul className={styles.mainNavList}>
                {mainMenu.map((item, index) => {
                    if (!item.link.title) return null;

                    return (
                        <li
                            key={index}
                            className={styles.mainNavItem}
                            data-testid={`navigation-item-${index}`}
                        >
                            <div className={styles.mainNavImage}>
                                <Image
                                    className={styles.image}
                                    height={400}
                                    width={400}
                                    sizes="25vw"
                                    src={item.image?.src as string}
                                    alt={item.image?.alt ?? 'Navigation image'}
                                />
                            </div>

                            {!!item.children?.length && (
                                <span
                                    className={styles.mainNavTitle}
                                    onClick={(e) => {
                                        e.currentTarget.parentElement?.classList.add(styles.active);
                                    }}
                                    aria-label={item.link?.title?.toLocaleLowerCase()}
                                >
                                    {item.link.title}
                                </span>
                            )}

                            <Link
                                href={item.link.url}
                                className={styles.mainNavLink}
                                activeClassName={styles.activeLink}
                                aria-label={item.link?.title?.toLocaleLowerCase()}
                            >
                                {item.link.title}
                            </Link>

                            {secondLevelItems(item, index)}
                        </li>
                    );
                })}

                {metaMenu?.links &&
                    metaMenu.links.map((link, linkIndex) => {
                        return (
                            <li key={linkIndex} className={styles.metaItem}>
                                <Link
                                    href={link.url}
                                    className={styles.metaLink}
                                    aria-label={link?.title?.toLocaleLowerCase()}
                                >
                                    <Typography variant="textLink-M" weight="regular">
                                        {link.title}
                                    </Typography>
                                    <Chevron />
                                </Link>
                            </li>
                        );
                    })}

                {metaMenu?.bottomLink?.url && (
                    <li className={styles.metaBottom}>
                        <Link
                            href={metaMenu.bottomLink.url}
                            className={styles.metaBottomLink}
                            aria-label={metaMenu.bottomLink?.title?.toLocaleLowerCase()}
                        >
                            <Typography variant="title-XS" weight="bold">
                                {metaMenu.bottomLink.title}
                            </Typography>
                        </Link>
                    </li>
                )}
            </ul>
        </nav>
    );
};

export default MegaMenu;
